//@ts-check
/**
 * The iOS app's device token, as the web layer sees it.
 *
 * Inside the iOS app the page is served from the app's bundle, not from the app's host, so the web
 * app's cookie session cannot work there. The app signs in with the same e-mailed code and is
 * given a device token instead (guides/AUTHENTICATION_FLOW.md#native-apps-device-tokens), which
 * every API call sends as `Authorization: Bearer`. The token lives in the Keychain, kept by the
 * app's own `TokenStore` plugin, in the App Group the share extension reads it from. It is held
 * here in memory and never written to IndexedDB, `localStorage` or a URL.
 *
 * On the web every function here is a no-op, and `@capacitor/core` is never loaded: the import
 * is dynamic and behind `isNativeShell()`, which the web build fixes as false.
 *
 * The plugin's methods (ios/App/App/TokenStore.swift): `get() → { token }`, `set({ token })`,
 * `clear()`, `device() → { model }`.
 */
import { isNativeShell } from "$lib/env.js";

/** @type {Promise<{ store: any }> | null} */
let pluginPromise = null;

/**
 * The plugin, registered once, wrapped in `{ store }`. Never resolve a promise with the plugin
 * itself: it is a proxy that answers to every property name, `then` included, so the promise
 * would take it for another promise and call `then` on the native side.
 * @returns {Promise<{ store: any }>}
 */
function tokenStore() {
  pluginPromise ??= import("@capacitor/core").then(({ registerPlugin }) => ({
    store: registerPlugin("TokenStore"),
  }));
  return pluginPromise;
}

// The token this device signed in with, read from the Keychain once; null when signed out.
/** @type {Promise<string | null> | null} */
let tokenPromise = null;

/**
 * The device token, or null — always null on the web.
 * @returns {Promise<string | null>}
 */
export function deviceToken() {
  if (!isNativeShell()) return Promise.resolve(null);
  tokenPromise ??= tokenStore()
    .then(({ store }) => store.get())
    .then((res) => res?.token || null)
    .catch((err) => {
      console.warn("Could not read the device token", err);
      return null;
    });
  return tokenPromise;
}

/**
 * Keep the token a sign-in returned.
 * @param {string} token
 */
export async function keepDeviceToken(token) {
  if (!isNativeShell()) return;
  const { store } = await tokenStore();
  await store.set({ token });
  tokenPromise = Promise.resolve(token);
}

/** Forget the token: on signing out, or when the server has stopped accepting it. */
export async function forgetDeviceToken() {
  if (!isNativeShell()) return;
  tokenPromise = Promise.resolve(null);
  try {
    const { store } = await tokenStore();
    await store.clear();
  } catch (err) {
    console.warn("Could not clear the device token", err);
  }
}

/**
 * What the device is called in the person's list of signed-in devices. iOS no longer tells apps
 * the name the owner gave the phone, so this is the model — "iPhone" — beside its sign-in date.
 * @returns {Promise<string>}
 */
export async function deviceName() {
  try {
    const { store } = await tokenStore();
    const res = await store.device();
    return res?.model || "iPhone";
  } catch {
    return "iPhone";
  }
}
